/**
 * Shared presentation metadata for a notification `type`, plus the relative
 * time renderer. Used by the popover preview, the inbox rows, and anywhere else
 * a notification surfaces so the icon/tint language stays identical.
 *
 * Icon tints are restrained, semantic, and NOT the brand accent — the single
 * brand-purple accent is reserved for the "unread" rail/dot so it never
 * competes with per-type differentiation (DESIGN.md §1).
 */
import {
  AtSign,
  Info,
  Printer,
  ShieldAlert,
  UserPlus,
  ArrowRight,
  type LucideIcon,
  MessageSquare,
} from 'lucide-react';
import { formatDistanceToNowStrict } from 'date-fns';
import { useDateFnsLocale } from '@/lib/format-dates';
import type { NotificationType } from './notifications-procedures';

interface TypeMeta {
  Icon: LucideIcon;
  /** Circle background + icon colour. Soft tint, functional, not decorative. */
  tint: string;
}

const TYPE_META: Record<NotificationType, TypeMeta> = {
  mention: { Icon: AtSign, tint: 'bg-violet-100 text-violet-600' },
  assigned: { Icon: UserPlus, tint: 'bg-blue-100 text-blue-600' },
  record_update: { Icon: ArrowRight, tint: 'bg-slate-100 text-slate-600' },
  push_blocked: { Icon: ShieldAlert, tint: 'bg-red-100 text-red-600' },
  system: { Icon: Info, tint: 'bg-amber-100 text-amber-600' },
  print_file_resolved: { Icon: Printer, tint: 'bg-emerald-100 text-emerald-600' },
  order_message: { Icon: MessageSquare, tint: 'bg-violet-100 text-violet-600' },
};

/** Fallback keeps an unknown future `type` renderable rather than crashing. */
const FALLBACK_META: TypeMeta = { Icon: Info, tint: 'bg-muted text-muted-foreground' };

function getNotificationMeta(type: NotificationType): TypeMeta {
  return TYPE_META[type] ?? FALLBACK_META;
}

/**
 * The tinted type-icon circle. `size` maps to the two contexts: `sm` for the
 * dense popover, `md` for the roomier inbox rows.
 */
export function NotificationTypeIcon({
  type,
  size = 'sm',
}: {
  type: NotificationType;
  size?: 'sm' | 'md';
}) {
  const { Icon, tint } = getNotificationMeta(type);
  const circle = size === 'md' ? 'h-9 w-9' : 'h-8 w-8';
  const glyph = size === 'md' ? 'h-5 w-5' : 'h-4 w-4';
  return (
    <span
      aria-hidden="true"
      className={`flex ${circle} shrink-0 items-center justify-center rounded-full ${tint}`}
    >
      <Icon className={glyph} />
    </span>
  );
}

/**
 * Relative time, locale-aware ("2h ago" style). Renders a `<time>` with a full
 * absolute `title`/`dateTime` so hover + assistive tech get the precise moment.
 */
export function RelativeTime({
  iso,
  className,
}: {
  iso: string;
  className?: string;
}) {
  const locale = useDateFnsLocale();
  const date = new Date(iso);
  const relative = formatDistanceToNowStrict(date, { addSuffix: true, locale });
  return (
    <time dateTime={iso} title={date.toLocaleString()} className={className}>
      {relative}
    </time>
  );
}
